import { injectable } from "inversify";
import {
  StorageKey,
  IStorage,
  StorageTypes,
  StorageValue,
  StorageCollection,
  StorageCollectionValue,
  STORAGE_COLLECTIONS,
} from "./storage.constants";

function promisifyIDBRequest<T>(request: IDBRequest<T>) {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = reject;
  });
}

const INDEX_DB_NAME = "TUVIS";
const INDEX_DB_VERSION = 2; // UPDATE THIS WHENEVER YOU CHANGE THE DB STRUCTURE

@injectable()
export class IndexdDBStorage implements IStorage {
  async get<K extends StorageKey | StorageKey[]>(key: K) {
    if (Array.isArray(key)) {
      const vals = await Promise.all(key.map((k) => this.getIndexed(INDEX_DB_NAME, k).then((value) => [k, value])));
      return Object.fromEntries(vals);
    }
    return await this.getIndexed(INDEX_DB_NAME, key);
  }
  async set<K extends StorageKey | Partial<StorageTypes>>(key: K, value?: K extends StorageKey ? StorageValue<K> : never) {
    if (typeof key === "string") {
      await this.setIndexed(INDEX_DB_NAME, key, value);
      this.notifyChange(key as StorageKey);
      return;
    }
    for (const [k, v] of Object.entries(key)) {
      await this.setIndexed(INDEX_DB_NAME, k as any, v);
      this.notifyChange(k as StorageKey);
    }
  }
  async remove<K extends StorageKey | StorageKey[]>(key: K) {
    if (Array.isArray(key)) {
      await Promise.all(key.map((k) => this.removeIndexed(INDEX_DB_NAME, k)));
      key.forEach((k) => this.notifyChange(k));
      return;
    }
    await this.removeIndexed(INDEX_DB_NAME, key);
    this.notifyChange(key);
  }
  async clear(_options?: { excludeCollections?: StorageCollection[] }): Promise<void> {
    this.indexedDB = undefined;
    await promisifyIDBRequest(indexedDB.deleteDatabase(INDEX_DB_NAME));
    this.changeListeners.forEach((listeners) => listeners.forEach((cb) => cb()));
  }

  async getIndexed<K extends StorageCollection | typeof INDEX_DB_NAME>(collection: K, key: string) {
    const db = await this.getIndexDB();
    const req = db.transaction([collection], "readonly").objectStore(collection).get(key);
    const data = await promisifyIDBRequest(req);
    return data?.value;
  }

  async setIndexed<K extends StorageCollection | typeof INDEX_DB_NAME>(
    collection: K,
    key: string,
    value: K extends StorageCollection ? StorageCollectionValue<K> : any
  ) {
    const db = await this.getIndexDB();
    const tx = db.transaction([collection], "readwrite");
    const store = tx.objectStore(collection);
    await promisifyIDBRequest(store?.put({ key, value }));
  }

  async removeIndexed<K extends StorageCollection | typeof INDEX_DB_NAME>(collection: K, key: string) {
    const db = await this.getIndexDB();
    const tx = db.transaction([collection], "readwrite");
    const store = tx.objectStore(collection);
    await promisifyIDBRequest(store?.delete(key));
  }

  private changeListeners = new Map<StorageKey, Set<() => void>>();
  onChange<K extends StorageKey>(key: K, cb: () => void) {
    const listeners = this.changeListeners.get(key) ?? new Set();
    listeners.add(cb);
    this.changeListeners.set(key, listeners);
  }
  private notifyChange(key: StorageKey) {
    this.changeListeners.get(key)?.forEach((cb) => cb());
  }

  private indexedDB: IDBDatabase | undefined;
  private async getIndexDB() {
    if (this.indexedDB) return this.indexedDB;
    const request = indexedDB.open(INDEX_DB_NAME, INDEX_DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      [...Object.keys(STORAGE_COLLECTIONS), INDEX_DB_NAME].forEach((collection) => {
        if (!db.objectStoreNames.contains(collection)) db.createObjectStore(collection, { keyPath: "key" });
      });
    };
    const result = await promisifyIDBRequest(request);
    this.indexedDB = result;
    return result;
  }
}
